import { ng } from "@common/ngds";
import { controlIdentityKey, getUtilizationValue } from "../components/SXRcControlsReport";
import {
  hasFireProtectionMaterial,
  hasSteelFireProtectionTemperature,
  type MemberMaterialGroup,
} from "./memberDesign";
import { timberProtectionHasLayerThicknesses, timberProtectionHasThickness } from "../components/FireSettings/shared";

// Helpers for presenting and applying AutoDesignSuggestions in the design review modal.
//
// A suggestion references a section and material by GUID (which resolve against the AutoDesign
// section/material databases the panel built for the run) and carries the reinforcement
// (rebars / strands / links) to apply. These helpers turn that into human-readable summaries and
// resolve the reference GUIDs, without needing a fully materialised project.

type Section = ng.sections.Section_json;
type Material = ng.material.Data_json;

/** Highest utilization across a suggestion's controls, as a ratio (1.0 == 100%). Null if none. */
export const getMaxUtilizationValue = (controls: ng.result.control.ControlData_json[] | undefined): number | null => {
  if (!controls?.length) return null;
  return controls.reduce<number | null>((max, control) => {
    // Reuse the controls report's parse so the modal and the report agree on utilization.
    const value = getUtilizationValue(control);
    if (value == null) return max;
    return max == null ? value : Math.max(max, value);
  }, null);
};

/** Format a utilization ratio as a percentage string, e.g. 0.873 -> "87.3%". */
export const formatUtilizationPercent = (value: number | null): string =>
  value != null ? `${(value * 100).toFixed(1)}%` : "—";

// Resolve a section's display name; fall back to the raw guid (not the "not found" dash) when the
// section exists but is unnamed, so an empty name never masquerades as "missing".
export const resolveSectionName = (guid: string | undefined, secDb: Section[] | undefined): string => {
  if (!guid) return "—";
  const found = secDb?.find((s) => s.id?.guid === guid);
  if (!found) return "—";
  return found.id?.name || guid;
};

export const resolveMaterialName = (guid: string | undefined, mtrlDb: Material[] | undefined): string => {
  if (!guid) return "—";
  const found = mtrlDb?.find((m) => m.id?.guid === guid);
  if (!found) return "—";
  return found.id?.name || guid;
};

export interface ReinforcementSummary {
  bars: string;
  strands: string;
  stirrups: string;
}

// Round a value to one decimal place, guarding against float artefacts (e.g. 9.299999999999999)
// leaking into a label. Does not change units.
const round1 = (value: number): number => Number(value.toFixed(1));

// Convert a metre value to millimetres, rounded to one decimal. Only link (stirrup) diameters and
// spacings are stored in metres in this model; rebar/strand diameters are already in mm. Mirrors
// getCurrentBeamReinforcementString in auxiliary.tsx, which scales links by 1e3 but prints rebar
// diameters verbatim.
const toMm = (metres: number): number => round1(metres * 1.0e3);

// Aggregate longitudinal bars/strands by diameter across ALL faces (top/bottom/left/right —
// columns carry side reinforcement too). Rebar/strand group diameters are already stored in
// millimetres (unlike link diameters), so they are only rounded here, not scaled. Diameters are
// sorted numerically so the same reinforcement always produces the same string regardless of
// layer/face emission order (the diff's "changed" flag compares these strings).
const aggregateBars = (
  reinf: { grps?: ng.reinf.Group_json[]; lays?: ng.reinf.Layer_json[] } | undefined | null,
): string => {
  const groups = reinf?.grps ?? [];
  const byDiameter = new Map<number, number>();
  for (const layer of reinf?.lays ?? []) {
    if (layer.num == null) continue;
    const group = groups.find((g) => g.id?.guid === layer.grpGuid);
    if (!group || group.diameter == null) continue;
    const diameter = round1(Number(group.diameter));
    byDiameter.set(diameter, (byDiameter.get(diameter) ?? 0) + Number(layer.num));
  }
  return [...byDiameter.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([diameter, count]) => `${count} Ø ${diameter}`)
    .join(" + ");
};

// Build a compact reinforcement summary (longitudinal bars, prestressing strands, stirrups) from
// raw rebar/strand/link data for the review diff.
export const summarizeReinforcement = (
  rebars: ng.rebar.Data_json | undefined | null,
  links: ng.link.Data_json | undefined | null,
  strands?: ng.strand.Data_json | undefined | null,
): ReinforcementSummary => {
  const stirrups = (links?.linkGrps ?? [])
    .filter((lg) => lg.diameter != null)
    .map((lg) => `Ø ${toMm(Number(lg.diameter))} s${toMm(Number(lg.s ?? 0))}`)
    .join(" + ");

  return { bars: aggregateBars(rebars), strands: aggregateBars(strands), stirrups };
};

/** The GUID of the section the primary element currently references (for the "before" column). */
export const getCurrentSectionGuid = (project: ng.frontend.column.Project_json | null | undefined): string | undefined =>
  project?.data?.input?.structures?.[0]?.elements?.[0]?.beam?.segments?.[0]?.layer?.start?.secGuid;

/** The GUID of the material the primary element currently references (for the "before" column). */
export const getCurrentMaterialGuid = (
  project: ng.frontend.column.Project_json | null | undefined,
): string | undefined => project?.data?.input?.structures?.[0]?.elements?.[0]?.beam?.mtrlGuid;

// The model is not consistent about the unit a cover is stored in: projGen seeds it in millimetres
// (DEFAULT_COVER_MM = 26) while the section lengths around it are metres, and the design service
// writes back whatever it worked in. Settled with the same magnitude test the project slice applies
// to section lengths (toMillimeters) — no cover reaches a whole metre, and none below a millimetre
// is a designed value. A missing, zero or unreadable cover yields null, i.e. "no cover to show",
// never a 0 mm that would read as a designed quantity.
const coverToMm = (value: unknown): number | null => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return round1(num > 1 ? num : num * 1.0e3);
};

/**
 * The concrete cover the primary element carries, in millimetres. Null when it carries none.
 *
 * Read for the review's "before" column, and — off the project the design service returned — for
 * its "after" column: the rc-design engine resolves the cover itself, per candidate bar diameter,
 * instead of using the one the project went in with, and writes what it settled on back onto the
 * element of the project it answers with. That project is therefore the only place the designed
 * cover appears, and it is applied to the project input on approval, so the review is where the
 * user gets to see it.
 *
 * Reads the bottom side, the one the reinforcement accordion's cover field shows. The engine emits
 * the same cover for all four zones; the other sides are a fallback for a payload that fills only
 * some of them.
 */
export const getConcreteCoverMm = (project: ng.frontend.column.Project_json | null | undefined): number | null => {
  const distances = project?.data?.input?.structures?.[0]?.elements?.[0]?.beam?.concrete?.distances ?? [];
  // In the *_json shape an enum is its name, not its number — convert before comparing.
  const isBottom = (distance: ng.design.concrete.CoverAndSpace_json): boolean =>
    ng.design.concrete.BeamSide_FromObject(distance.side) === ng.design.concrete.BeamSide.BEAM_SIDE_BOTTOM;

  const bottom = distances.find(isBottom);
  const anySide = distances.find((distance) => coverToMm(distance.cover) != null);
  return coverToMm(bottom?.cover) ?? coverToMm(anySide?.cover);
};

/** The primary element's current reinforcement (rebars + strands + links) for the "before" column. */
export const getCurrentReinforcement = (
  project: ng.frontend.column.Project_json | null | undefined,
): { rebars?: ng.rebar.Data_json; strands?: ng.strand.Data_json; links?: ng.link.Data_json } => {
  const concrete = project?.data?.input?.structures?.[0]?.elements?.[0]?.beam?.concrete;
  return { rebars: concrete?.rebars, strands: concrete?.strands, links: concrete?.links };
};

/**
 * The governing controls in a project's output: the worst utilization per distinct check, taken
 * across every load combination. Mirrors the "max" pane of the controls report (see
 * SXRcControlsReport) so the review and the report agree on what governs.
 *
 * Needed because the FEM-Design service answers a steel/timber design run with a suggestion whose
 * `controls` list is deliberately empty (it designs against a model holding only the section the run
 * starts from, so the controls it could quote there are not the recommendation's). The project it
 * returns is the code check of the recommendation, so the checks come out of that output instead.
 */
export const collectGoverningControls = (
  project: ng.frontend.column.Project_json | null | undefined,
): ng.result.control.ControlData_json[] => {
  const output = project?.data?.output;
  if (!output) return [];

  const utilization = (control: ng.result.control.ControlData_json): number => {
    const value = Number(control.utilization);
    return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
  };

  // The same identity the controls report groups on (control type + pane, never the title), so this
  // yields one entry per distinct check rather than one per title — see controlIdentityKey.
  const lcombTypeByGuid = new Map<string, string | undefined>();
  for (const lcomb of project?.data?.input?.structures?.[0]?.loading?.combinations ?? []) {
    const guid = lcomb?.id?.guid;
    if (guid) lcombTypeByGuid.set(guid, lcomb.type);
  }

  const worstByKey = new Map<string, ng.result.control.ControlData_json>();
  for (const lcomb of output.lcombs ?? []) {
    const lcombType = lcombTypeByGuid.get(lcomb.lcombGuid ?? "");
    for (const elementResult of lcomb.elementResults ?? []) {
      const result = elementResult.result?.result;
      if (!result) continue;
      for (const control of [
        ...(result.rc?.maxOfControls ?? []),
        ...(result.steel?.maxOfControls ?? []),
        ...(result.timber?.maxOfControls ?? []),
      ]) {
        const key = controlIdentityKey(control, lcombType);
        const existing = worstByKey.get(key);
        if (!existing || utilization(control) > utilization(existing)) {
          worstByKey.set(key, control);
        }
      }
    }
  }
  if (worstByKey.size > 0) return [...worstByKey.values()];

  // Legacy shape: controls hanging off the per-material design summaries instead.
  return [
    ...(output.rc ?? []).flatMap((summary) => summary.controls ?? []),
    ...(output.steel ?? []).flatMap((summary) => summary.controls ?? []),
    ...(output.timber ?? []).flatMap((summary) => summary.controls ?? []),
  ];
};

/** The AutoDesign section/material databases the panel cached for the last design run. */
export const getAutoDesignDbs = (
  project: ng.frontend.column.Project_json | null | undefined,
): { secDb: Section[]; mtrlDb: Material[] } => {
  const autoDesign = project?.settings?.endpointCache?.autoDesign as
    | ng.endpoint_args.common_api_functions.AutoDesign_json
    | undefined;
  return { secDb: autoDesign?.secDb ?? [], mtrlDb: autoDesign?.mtrlDb ?? [] };
};

// ---- Fire protection ------------------------------------------------------

// A protection thickness is stored in metres but read in millimetres, and a maximum member
// temperature in °C either way — the units the fire settings are edited in (see SteelFireSettings /
// TimberFireSettings), so the review shows the same numbers the user typed the limits as.
const THICKNESS_LABEL_KEYS = {
  thickness: "fire_protection_thickness_.label",
  inner: "fire_protection_thickness_inner_.label",
  outer: "fire_protection_thickness_outer_.label",
} as const;
const TEMPERATURE_LABEL_KEY = "maximum_member_temperature_.label";

/**
 * One fire-protection quantity a design run settles on: what a protected member ends up needing,
 * ready for a before/after row in the review diff. A steel member protected by a material and a
 * timber member with a single-layer protection each yield one thickness; a two-layer timber
 * protection yields an inner and an outer one; and a steel member checked against a maximum
 * temperature yields that temperature instead. An unprotected member yields none.
 */
export interface FireProtectionValue {
  /** Translation key for the row label — also the identity the before/after columns pair on. */
  labelKey: string;
  /** The value in the unit it is shown in: millimetres for a thickness, °C for a temperature. */
  value: number;
  unit: "mm" | "°C";
}

/** Format a fire-protection quantity for the diff, e.g. "13.5 mm". A dash when there is none. */
export const formatFireProtectionValue = (value: FireProtectionValue | undefined): string =>
  value ? `${round1(value.value)} ${value.unit}` : "—";

// A quantity of zero means "this member does not have that layer" rather than a designed value of
// nothing, so it yields no row at all — an unused inner/outer layer must not read as a change.
const thickness = (labelKey: string, metres: unknown): FireProtectionValue | null => {
  const mm = Number(metres) * 1.0e3;
  if (!Number.isFinite(mm) || mm <= 0) return null;
  return { labelKey, value: mm, unit: "mm" };
};

const temperature = (celsius: unknown): FireProtectionValue | null => {
  const value = Number(celsius);
  if (!Number.isFinite(value) || value <= 0) return null;
  return { labelKey: TEMPERATURE_LABEL_KEY, value, unit: "°C" };
};

const compact = (values: (FireProtectionValue | null)[]): FireProtectionValue[] =>
  values.filter((value): value is FireProtectionValue => value !== null);

// The thicknesses of a timber protection: only the ones its material type is actually described by,
// the same way the fire settings inputs offer them (see showTimberProtComp in TimberFireSettings).
// A single-layer material carries `thickness` alone, a two-layer one an inner and an outer
// thickness, and a user-defined or unprotected member none at all — the values the element happens
// to still carry for the other layers are leftovers, not designed quantities, so they yield no row.
const timberThicknesses = (
  properties: ng.fire_protection.timber.CharacteristicData_json | undefined | null,
): FireProtectionValue[] => {
  if (!properties) return [];
  const materialType =
    properties.materialType != null
      ? ng.fire_protection.timber.MaterialType_FromObject(properties.materialType)
      : ng.fire_protection.timber.MaterialType.MATERIAL_TYPE_UNSPECIFIED;

  if (timberProtectionHasThickness(materialType)) {
    return compact([thickness(THICKNESS_LABEL_KEYS.thickness, properties.thickness)]);
  }
  if (timberProtectionHasLayerThicknesses(materialType)) {
    return compact([
      thickness(THICKNESS_LABEL_KEYS.inner, properties.thicknessInner),
      thickness(THICKNESS_LABEL_KEYS.outer, properties.thicknessOuter),
    ]);
  }
  return [];
};

/**
 * The fire protection a project's element carries. Used for the diff's "before" column, and for its
 * "after" column when the suggestion itself does not carry the designed protection — the design
 * service writes the values it settled on onto the element of the project it returns, so the
 * returned project always tells what the run arrived at.
 */
export const getProjectFireProtection = (
  project: ng.frontend.column.Project_json | null | undefined,
  group: MemberMaterialGroup | null,
): FireProtectionValue[] => {
  const designSettings = project?.data?.input?.structures?.[0]?.elements?.[0]?.designSettings;

  if (group === "Steel") {
    if (hasSteelFireProtectionTemperature(project, group)) {
      return compact([temperature(designSettings?.steel?.fire?.temperature)]);
    }
    if (hasFireProtectionMaterial(project, group)) {
      return compact([
        thickness(THICKNESS_LABEL_KEYS.thickness, designSettings?.steel?.fire?.protectingMaterial?.thickness),
      ]);
    }
    return [];
  }

  if (group === "Timber") {
    if (!hasFireProtectionMaterial(project, group)) return [];
    return timberThicknesses(designSettings?.timber?.fire?.protection?.mtrl?.properties);
  }

  return [];
};

/**
 * The fire protection a suggestion was designed with, as the design service reported it. Empty for
 * an unprotected member, for concrete (whose protection is not designed here), and for a service
 * that predates the suggestion carrying it — callers fall back to the returned project then.
 */
export const getSuggestedFireProtection = (
  suggestion: ng.endpoint_args.common_api_functions.AutoDesignSuggestion_json,
  group: MemberMaterialGroup | null,
): FireProtectionValue[] => {
  if (group === "Steel") {
    const steel = suggestion.steel;
    if (!steel) return [];
    // The two arms of the protection_type oneof — a member has one or the other, never both.
    if (steel.temperature != null) return compact([temperature(steel.temperature)]);
    return compact([thickness(THICKNESS_LABEL_KEYS.thickness, steel.protectingMaterial?.thickness)]);
  }

  if (group === "Timber") return timberThicknesses(suggestion.timber?.protectingMaterial);

  return [];
};
